/**
 * Справочник категорий `/catalog/categories` (§9.1).
 *
 * Правки применяются сразу, без черновика (§9), поэтому каждое необратимое
 * действие объясняет последствие числом: «У категории 53 привязки…».
 * Переименование — инлайновое: привязки живут по id категории, а не по имени,
 * поэтому имя меняется и у брендов, и в истории, ничего не ломая.
 * После любой правки перечитываем срез брендов: в таблице названия категорий
 * показаны строками, и они обязаны совпасть со справочником.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Category } from '@shared/types.ts'
import {
  Badge, Button, EmptyState, IconPlus, Input, Modal, Skeleton,
  type MenuItem,
} from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { AdminError, AdminPage, RowActions } from '../admin/AdminPage.js'
import { adminTexts } from '../admin/texts.admin.js'
import { archiveCategoryForce, errorText, needsConfirmation } from '../admin/admin-api.js'
import { MergeCategoriesDialog } from './MergeCategoriesDialog.js'
import './CategoriesScreen.css'

const t = adminTexts.categories

/** Состояние модалки архивации: категория и число привязок, которое покажем. */
interface ArchiveTarget {
  category: Category
  brands: number
}

export function CategoriesScreen() {
  const toast = useApp((s) => s.toast)
  const reloadBrands = useApp((s) => s.reloadBrands)

  const [items, setItems] = useState<Category[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)

  const [renameId, setRenameId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameRef = useRef<HTMLInputElement | null>(null)
  /** Переименование закрывается один раз: Enter, Esc и потеря фокуса не спорят. */
  const renameDone = useRef(true)

  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null)
  const [mergeSource, setMergeSource] = useState<Category | null>(null)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const res = await api.categories()
      setItems(res.items)
    } catch (err) {
      setLoadError(errorText(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (renameId !== null) renameRef.current?.focus()
  }, [renameId])

  const activeCount = useMemo(
    () => (items ?? []).filter((c) => c.status === 'active').length,
    [items],
  )

  /** Общий хвост любой правки справочника: перечитать список и срез брендов. */
  const refresh = useCallback(async () => {
    await load()
    try {
      await reloadBrands()
    } catch {
      /* срез перечитается при следующем обращении — справочник уже верен */
    }
  }, [load, reloadBrands])

  async function create() {
    const name = createName.trim()
    if (name.length === 0) {
      setCreateError(adminTexts.common.required)
      return
    }
    setBusy(true)
    try {
      await api.createCategory(name)
      setCreateOpen(false)
      setCreateName('')
      setCreateError(null)
      toast('success', t.toastCreated(name))
      await refresh()
    } catch (err) {
      setCreateError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function finishRename(category: Category, apply: boolean) {
    if (renameDone.current) return
    renameDone.current = true
    setRenameId(null)
    const name = renameValue.trim()
    if (!apply || name.length === 0 || name === category.name) return
    try {
      await api.renameCategory(category.id, name)
      toast('success', t.toastRenamed(name))
      await refresh()
    } catch (err) {
      toast('error', errorText(err))
    }
  }

  /** Архивация: сначала без force, по требованию сервера — через подтверждение. */
  async function archive(category: Category, force: boolean) {
    setBusy(true)
    try {
      if (force) await archiveCategoryForce(category.id)
      else await api.archiveCategory(category.id)
      setArchiveTarget(null)
      toast('success', t.toastArchived(category.name))
      await refresh()
    } catch (err) {
      const brands = needsConfirmation(err)
      if (brands !== null && !force) {
        // Сервер сам назвал число брендов — показываем подтверждение с ним.
        setArchiveTarget({ category, brands })
        return
      }
      // Окно подтверждения не закрываем: человек видит, на чём споткнулся.
      if (!force) setArchiveTarget(null)
      toast('error', errorText(err))
    } finally {
      setBusy(false)
    }
  }

  function menuItems(category: Category): MenuItem[] {
    const archived = category.status === 'archived'
    const usage = category.usageCount ?? 0
    return [
      { id: 'rename', label: t.menuRename },
      {
        id: 'archive',
        label: t.menuArchive,
        disabled: archived,
        reason: archived ? t.archiveReasonArchived : undefined,
      },
      {
        id: 'merge',
        label: t.menuMerge,
        disabled: archived,
        reason: archived ? t.mergeReasonArchived : undefined,
      },
      {
        id: 'delete',
        label: t.menuDelete,
        danger: true,
        disabled: true,
        separatorBefore: true,
        reason: usage > 0 ? t.deleteReasonUsed(usage) : t.deleteReasonUnsupported,
      },
    ]
  }

  function onMenuSelect(category: Category, id: string) {
    if (id === 'rename') {
      setRenameValue(category.name)
      renameDone.current = false
      setRenameId(category.id)
      return
    }
    if (id === 'merge') {
      setMergeSource(category)
      return
    }
    if (id === 'archive') {
      const usage = category.usageCount ?? 0
      if (usage > 0) setArchiveTarget({ category, brands: usage })
      else void archive(category, false)
    }
  }

  const renaming = items?.find((c) => c.id === renameId) ?? null

  return (
    <AdminPage
      wide
      title={t.title}
      meta={items ? t.count(activeCount) : undefined}
      subtitle={adminTexts.common.immediate}
      action={
        <Button
          variant="primary"
          iconBefore={<IconPlus size={16} />}
          onClick={() => {
            setCreateError(null)
            setCreateName('')
            setCreateOpen(true)
          }}
        >
          {t.add}
        </Button>
      }
    >
      {loadError ? <AdminError message={loadError} onRetry={() => { void load() }} /> : null}

      {!loadError && items === null ? (
        <div className="adm-table-wrap">
          <Skeleton rows={8} columns={[240, 80, 100, 40]} label={adminTexts.common.loading} />
        </div>
      ) : null}

      {!loadError && items !== null && items.length === 0 ? (
        <EmptyState
          title={t.emptyTitle}
          text={t.emptyText}
          actions={<Button variant="secondary" onClick={() => setCreateOpen(true)}>{t.add}</Button>}
        />
      ) : null}

      {!loadError && items !== null && items.length > 0 ? (
        <>
          <div className="adm-table-wrap">
            <table className="adm-table cat-table">
              <colgroup>
                <col className="cat-col--name" />
                <col className="cat-col--brands" />
                <col className="cat-col--status" />
                <col className="cat-col--actions" />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="t-col">{t.colName}</th>
                  <th scope="col" className="t-col adm-cell--num">{t.colBrands}</th>
                  <th scope="col" className="t-col">{t.colStatus}</th>
                  <th scope="col" className="t-col">
                    <span className="visually-hidden">{t.colActions}</span>
                  </th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {items.map((category) => {
                  const usage = category.usageCount ?? 0
                  const archived = category.status === 'archived'
                  return (
                    <tr key={category.id} className={archived ? 'adm-row--archived' : undefined}>
                      <td>
                        {renameId === category.id ? (
                          <div className="adm-inline">
                            <Input
                              ref={renameRef}
                              size="cell"
                              value={renameValue}
                              aria-label={t.nameLabel}
                              onChange={(event) => setRenameValue(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault()
                                  void finishRename(category, true)
                                }
                                if (event.key === 'Escape') {
                                  event.preventDefault()
                                  void finishRename(category, false)
                                }
                              }}
                              onBlur={() => { void finishRename(category, true) }}
                            />
                          </div>
                        ) : (
                          <div className="adm-name">
                            <span className="adm-name__text">{category.name}</span>
                          </div>
                        )}
                      </td>
                      <td className="adm-cell--num">
                        {usage > 0 ? usage : <span className="cat-zero">0</span>}
                      </td>
                      <td>
                        <Badge variant={archived ? 'neutral' : 'success'}>
                          {archived ? adminTexts.common.statusArchived : adminTexts.common.statusActive}
                        </Badge>
                      </td>
                      <td className="adm-cell--actions">
                        <RowActions
                          label={`${adminTexts.common.rowMenu}: ${category.name}`}
                          items={menuItems(category)}
                          onSelect={(id) => onMenuSelect(category, id)}
                        />
                      </td>
                      <td />
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Пояснение инлайнового переименования: в строку 36 px оно не влезает. */}
          <p className="cat-renaming t-meta" aria-live="polite">
            {renaming ? `${t.renameConsequence} ${t.renameHint}` : ''}
          </p>
        </>
      ) : null}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t.createTitle}
        size="s"
        onConfirm={() => { void create() }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {adminTexts.common.cancel}
            </Button>
            <Button variant="primary" loading={busy} onClick={() => { void create() }}>
              {t.createSubmit}
            </Button>
          </>
        }
      >
        <Input
          autoFocus
          value={createName}
          aria-label={t.nameLabel}
          placeholder={t.namePlaceholder}
          error={createError ?? undefined}
          hint={t.createConsequence}
          onChange={(event) => {
            setCreateName(event.target.value)
            setCreateError(null)
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            void create()
          }}
        />
      </Modal>

      <Modal
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        title={t.archiveTitle}
        size="s"
        dismissible={false}
        footerNote={t.archiveNote}
        footer={
          <>
            <Button variant="secondary" onClick={() => setArchiveTarget(null)}>
              {adminTexts.common.cancel}
            </Button>
            <Button
              variant="danger-ghost"
              loading={busy}
              onClick={() => {
                if (archiveTarget) void archive(archiveTarget.category, true)
              }}
            >
              {t.archiveSubmit}
            </Button>
          </>
        }
      >
        {archiveTarget ? (
          <>
            <p className="adm-modal-text">{t.archiveQuestion(archiveTarget.category.name)}</p>
            <p className="adm-modal-text">
              {archiveTarget.brands > 0 ? t.archiveUsed(archiveTarget.brands) : t.archiveFree}
            </p>
          </>
        ) : null}
      </Modal>

      <MergeCategoriesDialog
        source={mergeSource}
        categories={items ?? []}
        onClose={() => setMergeSource(null)}
        onMerged={async (source, target) => {
          setMergeSource(null)
          toast('success', adminTexts.merge.toastDone(source.name, target.name))
          await refresh()
        }}
      />
    </AdminPage>
  )
}
